/**
 * Remote pose runtime: the exercise video track goes to the pose service over WebRTC, the
 * service runs FastSAM3D on its GPU and returns one `model-result` per processed frame on the
 * `results` data channel. The handle has the same shape as the other rungs in pose-runtime.js
 * (`{ kind, delegate, detect, warmup, dispose }`), so use-predict-worker's pull loop, throttle,
 * skeleton overlay and WebSocket payload work unchanged:
 *
 *   - `detect({ bitmap })` closes the bitmap (the frame already went over WebRTC) and resolves
 *     the newest unread result as `{ durationMs, landmarks: [mediapipe_keypoints], mhr, ... }`,
 *     or `{ dropped: true }` when nothing new arrived since the last tick.
 *   - `landmarks[0]` is the model's keypoints on MediaPipe-33 slots, normalised over the frame
 *     the model saw, un-mirrored: drawPoseSkeleton draws it as it draws MediaPipe.
 *   - `mhr` is the model's own output (joint rotations, 2D/3D keypoints); the hook relays it to
 *     the backend inside the `evaluation` message for the rotation matcher.
 *
 * Protocol (backend/pose_service/docs/architecture.md): a WebSocket for signalling
 * (`offer` out, `answer` and `ice-candidate` in), the `results` channel created before the
 * offer, one sendonly video transceiver, H.264 preferred, the sender's encoding capped to
 * TARGET_WIDTH and TARGET_FPS. The video track is the one the exercise screen already shows (the
 * camera store's stream, or an uploaded clip attached by the hook through `attachSource(video)`).
 */

import {
  TARGET_FPS,
  TARGET_WIDTH,
  capWidth,
  elementStream,
  liveStream,
  readResultMessage,
  storeStream,
  takeResult,
  waitForStream,
} from './remote-pose-common';

const LINK_TIMEOUT_MS = 20000;
const DEFAULT_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];

function preferCodec(transceiver, want = 'h264') {
  if (!transceiver.setCodecPreferences || !window.RTCRtpSender?.getCapabilities) {
    return;
  }
  const caps = RTCRtpSender.getCapabilities('video');
  if (!caps) {
    return;
  }
  const hit = codec => codec.mimeType.toLowerCase() === `video/${want}`;
  try {
    transceiver.setCodecPreferences([
      ...caps.codecs.filter(hit),
      ...caps.codecs.filter(codec => !hit(codec)),
    ]);
  } catch (error) {
    console.warn('[remote-pose] codec preference failed', error);
  }
}

/**
 * Build the remote runtime. Resolves once the data channel is open; rejects when the service
 * cannot be reached or no video stream appears within STREAM_WAIT_MS.
 */
export async function createRemoteRuntime({
  url,
  sessionId = '',
  onProgress = () => {},
  iceServers = DEFAULT_ICE_SERVERS,
  targetWidth = TARGET_WIDTH,
  targetFps = TARGET_FPS,
} = {}) {
  if (!url) {
    throw new Error('remote pose runtime: no pose service URL (FEATURE_POSE_SERVICE_URL)');
  }
  onProgress({ phase: 'model', loaded: 0, total: 0 });

  const state = {
    status: 'waiting-for-stream',
    attachedStream: null,
    latest: null, // the newest model-result
    unread: false,
    results: 0,
    heartbeats: 0,
    lastResultAt: 0,
    error: null,
    trackSwaps: 0,
  };
  let followTimer = null;
  const handle = {
    kind: 'remote',
    delegate: 'GPU',
    pipeline: { thread: 'remote', inputTransfer: 'webrtc-video', offscreenCanvas: false },
    attachSource(videoElement) {
      const stream = elementStream(videoElement);
      if (stream) {
        state.attachedStream = stream;
      }
    },
    detect({ bitmap }) {
      bitmap?.close?.();
      return Promise.resolve(takeResult(state));
    },
    warmup({ bitmap }) {
      bitmap?.close?.();
    },
    status() {
      return { ...state, latest: undefined };
    },
    dispose() {
      state.status = 'disposed';
      clearInterval(followTimer);
      try {
        dataChannel?.close();
      } catch (error) {
        /* ignore */
      }
      try {
        pc?.close();
      } catch (error) {
        /* ignore */
      }
      try {
        ws?.close();
      } catch (error) {
        /* ignore */
      }
    },
  };

  // 1. the video stream: the camera store's, or one attached by the hook
  const stream = await waitForStream(state, 'remote pose runtime');

  // 2. signalling + peer connection
  const query = `?demo=kine${sessionId ? `&session=${encodeURIComponent(sessionId)}` : ''}`;
  const ws = new WebSocket(`${url.replace(/\/+$/, '')}/${query}`);
  const pc = new RTCPeerConnection({ iceServers });
  const dataChannel = pc.createDataChannel('results'); // before the offer, by protocol
  const transceiver = pc.addTransceiver(stream.getVideoTracks()[0], {
    direction: 'sendonly',
    streams: [stream],
  });
  preferCodec(transceiver);

  // The camera can be re-opened after the stream was taken (the exercise page acquires its own
  // stream and stops the earlier one): the sender would keep an ended track and the service would
  // see one frame. Keep sending whichever video track is live now: the attached clip's, else the
  // camera store's.
  let swapping = false;
  const followCamera = async () => {
    const sender = transceiver.sender;
    const source = liveStream(state.attachedStream) || storeStream();
    const track = source?.getVideoTracks().find(t => t.readyState === 'live');
    if (swapping || state.status === 'disposed' || !track || sender.track === track) {
      return;
    }
    swapping = true;
    try {
      await sender.replaceTrack(track);
      state.trackSwaps += 1;
      track.addEventListener('ended', followCamera, { once: true });
      await capWidth(sender, targetWidth, targetFps);
    } catch (error) {
      console.warn('[remote-pose] could not switch to the current camera track', error);
    } finally {
      swapping = false;
    }
  };
  stream.getVideoTracks()[0].addEventListener('ended', followCamera, { once: true });

  dataChannel.onmessage = event => readResultMessage(state, event.data);
  pc.onicecandidate = event => {
    if (event.candidate && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'ice-candidate', candidate: event.candidate.toJSON() }));
    }
  };
  pc.onconnectionstatechange = () => {
    state.status = `pc:${pc.connectionState}`;
  };

  const linkUp = new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('remote pose runtime: the pose service did not answer in time')),
      LINK_TIMEOUT_MS
    );
    dataChannel.onopen = () => {
      clearTimeout(timer);
      state.status = 'streaming';
      resolve();
    };
    ws.onopen = async () => {
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        ws.send(JSON.stringify({ type: 'offer', sdp: pc.localDescription.sdp }));
      } catch (error) {
        clearTimeout(timer);
        reject(error);
      }
    };
    ws.onmessage = async event => {
      try {
        const message = JSON.parse(event.data);
        if (message.type === 'answer') {
          await pc.setRemoteDescription({ type: 'answer', sdp: message.sdp });
          await capWidth(transceiver.sender, targetWidth, targetFps);
        } else if (message.type === 'ice-candidate' && message.candidate) {
          await pc.addIceCandidate(message.candidate);
        } else if (message.type === 'demo-error') {
          clearTimeout(timer);
          reject(new Error(`pose service refused: ${message.error}`));
        }
      } catch (error) {
        console.warn('[remote-pose] signalling error', error);
      }
    };
    ws.onerror = () => {
      clearTimeout(timer);
      reject(new Error(`remote pose runtime: cannot reach the pose service at ${url}`));
    };
    ws.onclose = () => {
      if (state.status !== 'disposed') {
        state.status = 'signalling-closed';
      }
    };
  });

  try {
    await linkUp;
  } catch (error) {
    handle.dispose();
    throw error;
  }
  followTimer = setInterval(followCamera, 500);
  onProgress({ phase: 'model', loaded: 1, total: 1 });
  return handle;
}
